'use client'
import { useEffect, useRef, useState } from 'react'
import type { SectionConfig } from '@/types/site'
import { elementProps } from '@/lib/elementHelpers'

interface EmbedSectionProps {
  config: SectionConfig
  isEditing?: boolean
}

export function EmbedSection({ config }: EmbedSectionProps) {
  const content = config.content as Record<string, unknown>
  const { accentColor } = config.style
  const accent = accentColor ?? '#C2A66B'
  const title = (content.title as string) || 'Réservation'
  const subtitle = (content.subtitle as string) || ''
  const embedUrl = (content.embedUrl as string) || ''
  const footerText = (content.footerText as string) || ''
  const eventName = (content.eventName as string) || 'Soirée d\'Ouverture'

  // Descripteur d'affichage RÉSOLU par l'adaptateur billetterie (back-office) :
  // le site ne connaît pas le prestataire, il applique ce qu'on lui donne.
  //  - mode 'embed'    : iframe de hauteur `minHeight`, grandit via postMessage.
  //  - mode 'redirect' : bouton vers la page hébergée (défaut HelloAsso — la
  //                      restriction CORS sur la mire d'auth rend l'embed risqué).
  const mode = (content.mode as string) === 'redirect' ? 'redirect' : 'embed'
  const minHeight = typeof content.minHeight === 'number' ? content.minHeight : 800

  // Auto-resize GÉNÉRIQUE : on écoute un postMessage {height} standard, sans
  // charger le moindre script de prestataire. Billetweb reste à `minHeight` ;
  // un prestataire qui émet sa hauteur (HelloAsso en embed) fait grandir l'iframe.
  const [height, setHeight] = useState(minHeight)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  useEffect(() => {
    if (mode !== 'embed') return
    function onMessage(e: MessageEvent) {
      const data = e.data as { height?: number } | undefined
      const h = typeof data?.height === 'number' ? data.height : undefined
      if (h && h > 200 && h < 20000) setHeight(Math.max(h, minHeight))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [mode, minHeight])

  return (
    <section
      {...elementProps(config.id, 'wrapper', 'container', 'Embed Section')}
      style={{
        backgroundColor: '#021734',
        fontFamily: 'var(--font-body, inherit)',
        minHeight: '100vh',
      }}
    >
      {/* Elegant top bar */}
      <div
        style={{
          borderBottom: `1px solid rgba(255,255,255,0.06)`,
          padding: '20px 24px',
        }}
      >
        <div style={{ maxWidth: '680px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a
            href="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: 'clamp(12px, 0.75rem + 0.2vw, 13px)',
              color: 'rgba(255,255,255,0.5)',
              textDecoration: 'none',
              fontWeight: 500,
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#ffffff')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
            </svg>
            Retour au site
          </a>
          <span style={{ fontSize: 'clamp(10px, 0.625rem + 0.2vw, 11px)', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 500 }}>
            {eventName}
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '48px 24px 80px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <p
            style={{
              fontSize: 'clamp(10px, 0.625rem + 0.2vw, 11px)',
              fontWeight: 600,
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: accent,
              margin: '0 0 12px',
            }}
          >
            Réservation
          </p>
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            style={{
              fontSize: 'clamp(26px, 4vw, 36px)',
              fontWeight: 600,
              color: '#ffffff',
              margin: '0 0 10px',
              letterSpacing: '-0.02em',
              fontFamily: 'var(--font-heading, inherit)',
            }}
          >
            {title}
          </h2>
          {subtitle && (
            <p
              {...elementProps(config.id, 'subtitle', 'text')}
              style={{
                fontSize: 'clamp(13px, 0.8125rem + 0.3vw, 15px)',
                color: 'rgba(255,255,255,0.35)',
                margin: '0 0 24px',
              }}
            >
              {subtitle}
            </p>
          )}
          {/* Accent divider */}
          <div style={{ width: '50px', height: '1px', margin: '0 auto', background: accent }} />
        </div>

        {/* Billetterie : selon le mode résolu par l'adaptateur.
            Aucun script de prestataire — voir la note en tête de composant. */}
        {!embedUrl ? (
          <div
            style={{
              textAlign: 'center',
              padding: '44px 28px',
              borderRadius: '14px',
              border: `1px solid ${accent}33`,
              background: 'rgba(255,255,255,0.03)',
            }}
          >
            <p style={{ fontSize: 'clamp(15px, 0.9rem + 0.4vw, 18px)', color: '#ffffff', fontWeight: 500, margin: '0 0 10px', fontFamily: 'var(--font-heading, inherit)' }}>
              La billetterie en ligne ouvre très prochainement
            </p>
            <p style={{ fontSize: 'clamp(12px, 0.75rem + 0.2vw, 14px)', color: 'rgba(255,255,255,0.55)', margin: '0 0 22px', lineHeight: 1.7 }}>
              Les réservations pour le Grand Gala des Étoiles seront disponibles ici même.<br />Pour être prévenu·e dès l&apos;ouverture, écrivez-nous.
            </p>
            <a
              href="mailto:galadesetoiles@rstars.fr?subject=Réservation%20Grand%20Gala%20des%20Étoiles%20—%203%20octobre%202026"
              style={{
                display: 'inline-block',
                padding: '14px 34px',
                borderRadius: '8px',
                background: accent,
                color: '#021734',
                textDecoration: 'none',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: 'clamp(12px, 0.75rem + 0.2vw, 13px)',
              }}
            >
              Être prévenu·e de l&apos;ouverture
            </a>
          </div>
        ) : mode === 'embed' ? (
          <div style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <iframe
              ref={iframeRef}
              title="Vente de billets en ligne"
              src={embedUrl}
              style={{ display: 'block', width: '100%', height: `${height}px`, border: 'none' }}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <a
              href={embedUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block',
                padding: '16px 40px',
                borderRadius: '8px',
                background: accent,
                color: '#0a0a0a',
                textDecoration: 'none',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: 'clamp(12px, 0.75rem + 0.2vw, 14px)',
              }}
            >
              Réserver ma place
            </a>
            <p style={{ marginTop: '16px', fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
              La réservation s&apos;ouvre sur la billetterie sécurisée du prestataire.
            </p>
          </div>
        )}

        {/* Footer text */}
        {footerText && (
          <p style={{ textAlign: 'center', marginTop: '32px', fontSize: 'clamp(11px, 0.6875rem + 0.2vw, 12px)', color: 'rgba(255,255,255,0.18)' }}>
            {footerText}
          </p>
        )}
      </div>
    </section>
  )
}

export const embedMeta = {
  type: 'embed',
  label: 'Embed',
  icon: '🔗',
  variants: ['ticketing'],
  defaultVariant: 'ticketing',
  defaultContent: {},
}
